import { BaseSyntheticEvent, FC, useEffect, useMemo } from "react";
import { InputProps } from "./input";

interface HookOptions<P> extends Pick<InputProps, "name" | "value" | "onChange"> {
  props: Omit<P, "name" | "value" | "onChange">;
  input: HTMLInputElement;
}

interface HookedWithInputOptions<P> {
  displayName: string;
  Component: FC<P>;
  useHook: (options: HookOptions<P>) => P & globalThis.JSX.IntrinsicAttributes;
}

export function hookedWithInput<P>({ displayName, Component, useHook }: HookedWithInputOptions<P>):
  FC<Omit<P, "name" | "value" | "onChange"> & Pick<InputProps, "name" | "value" | "onChange">> {
  Component.displayName = displayName;

  return function HookedWithInput(originalProps) {
    const { name, value, onChange } = originalProps;

    const props: HookOptions<P>["props"] = {
      ...originalProps,
      name: null,
      value: null,
      onChange: null,
    };

    const input = useMemo(() => document.createElement("input"), []);

    useEffect(() => {
      if (onChange) {
        const listener = (nativeEvent: Event) => {
          const synteticEvent = createSynteticEvent(input, nativeEvent);
          onChange(synteticEvent);
        };

        input.addEventListener("change", listener);

        return () => input.removeEventListener("change", listener);
      }
    }, [input, onChange]);

    const propsHooked = useHook({ props, input, name, value, onChange });

    return <Component {...propsHooked} />;
  };
}

function createSynteticEvent(input: HTMLInputElement, e: Event):
  BaseSyntheticEvent<Event, HTMLInputElement, HTMLInputElement> {
  let isPropagationStopped = false;

  return {
    nativeEvent: e,
    get currentTarget() {
      return input;
    },
    get bubbles() {
      return e.bubbles;
    },
    get cancelable() {
      return e.cancelable;
    },
    get defaultPrevented() {
      return e.defaultPrevented;
    },
    get eventPhase() {
      return e.eventPhase;
    },
    get isTrusted() {
      return e.isTrusted;
    },
    preventDefault() {
      e.preventDefault();
    },
    isDefaultPrevented() {
      return e.defaultPrevented;
    },
    stopPropagation() {
      isPropagationStopped = true;
      return e.stopPropagation();
    },
    isPropagationStopped() {
      return isPropagationStopped;
    },
    persist() {
    },
    get target() {
      return input;
    },
    get timeStamp() {
      return e.timeStamp;
    },
    get type() {
      return e.type;
    },
  };
}

